/* eslint-disable max-len */
import { Map, OrderedMap } from 'immutable';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import r from 'render-dom';
import styled from 'styled-components';
import { isEmail } from 'validator';

import Button from 'components/App/Dashboard/ui/Button';
import OverlayCard from 'components/shared/OverlayCard';
import Flex from 'components/ui/Flex';
import { smallMax } from 'constants/mediaQueries';
import { useMeContext } from 'hooks';
import ListingRecord from 'records/listing';
import { getAsinFromURL } from 'utils/helpers';
import { setJwt } from 'utils/jwt';
import { trackEvent } from 'utils/track';

import Indicator from './Indicator';
import ProductSetup from './ProductSetup';
import SignUp from './SignUp';

export const SignUpText = styled.div`
    font-size: 20px;
    font-weight: 700;
    margin-bottom: 20px;
    margin-top: 40px;
`;

const CloseButton = styled.i.attrs({ className: 'icon-times' })`
    cursor: pointer;
    font-size: 28px;
    padding: 0;
    position: absolute;
    right: 10px;
    top: 12px;
`;

const ComponentContent = styled.div`
    padding: 20px 0 10px;
    width: 100%;

    @media(${smallMax}) {
        margin-bottom: 50px;
        padding: 10px 0 0;
    }
`;

const Footer = styled.div`
    align-items: center;
    display: flex;
    padding-top: 20px;
    width: 100%;

    @media(${smallMax}) {
        align-items: center;
        background: #fff;
        border-radius: 0 0 10px 10px;
        border-top: 2px solid #f5f5f5;
        bottom: 0;
        display: flex;
        box-sizing: border-box;
        padding: 20px 10px;
        position: fixed;
        width: 98vw;
    }
`;

const Agreement = styled.div`
    color: #bbb;
    font-size: 12px;
    position: relative;
    top: 20px;
`;

const Skip = styled(Link).attrs({ to: '/' })`margin-right: 30px;`;

const propTypes = {
    cardBrand: PropTypes.string,
    cardLastFour: PropTypes.string,
    companyId: PropTypes.number,
    createListing: PropTypes.func.isRequired,
    fetchMe: PropTypes.func.isRequired,
    fetchProducts: PropTypes.func.isRequired,
    hasFetchedProducts: PropTypes.bool.isRequired,
    hasProducts: PropTypes.bool.isRequired,
    jwt: PropTypes.string,
    onClose: PropTypes.func.isRequired,
    onSignUp: PropTypes.func.isRequired,
    patchCompany: PropTypes.func.isRequired,
    patchMe: PropTypes.func.isRequired,
    postKeyword: PropTypes.func.isRequired,
    postSubscription: PropTypes.func.isRequired,
    putSubscription: PropTypes.func.isRequired,
    signup: PropTypes.func.isRequired,
    updateCard: PropTypes.func.isRequired,
};

export default function AccountSetup({
    createListing,
    hasProducts,
    jwt,
    onClose = Function.prototype,
    onSignUp = Function.prototype,
    patchCompany,
    plans,
    postKeyword,
    postSubscription,
    show = true,
    showSkeleton = false,
    signup,
}) {
    const [currentStep, setCurrentStep] = useState('account');
    const [accountInfo, setAccountInfo] = useState(Map());
    const [productSetupForm, setProductSetupForm] = useState(Map());
    const [validation, setValidation] = useState(Map());
    const [showLoading, setShowLoading] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const [me, fetchMe] = useMeContext();
    const { push: navigate } = useHistory();
    const minCharacters = (inputName, characters) => `${inputName} must be at least ${characters} characters`;
    const accountInfoValidation = Map({
        companyName: accountInfo.get('companyName', '').length > 1 || minCharacters('Company name', '2'),
        email: isEmail(accountInfo.get('email', '')) || 'Please add a valid email address',
        fullName: accountInfo.get('fullName', '').length > 1 || minCharacters('Full name', '2'),
        interestedIn: accountInfo.get('interestedIn', '').length > 0 || 'Please choose an option',
        password: accountInfo.get('password', '').length > 7 || minCharacters('Password', '8'),
    }).filterNot((field) => field === true);
    const asin = getAsinFromURL(productSetupForm.get('amazonUrl', '')) || '';
    const productSetupValidation = Map({
        asin: asin.length > 9 || 'ASIN is required',
        keyword: productSetupForm.get('keyword', '').trim().length > 2 || 'Keyword is required',
    }).filterNot((field) => field === true);
    const clearValidationField = (field) => setValidation(validation.set(field, true));
    const isLoggedIn = jwt.length > 0;
    const freePlan = plans.find((p) => p.get('price') === 0);
    const steps = OrderedMap({
        account: {
            buttonText: 'Next',
            component: r(SignUp, {
                clearValidationField,
                form: accountInfo,
                updateForm: setAccountInfo,
                validation,
            }),
            isLoading: showLoading,
            onNext: async () => {
                setShowLoading(true);
                const email = accountInfo.get('email');
                const password = accountInfo.get('password');
                const [firstName, lastName = ''] = accountInfo.get('fullName').split(' ');

                const signUpRes = await signup(Map({
                    email,
                    firstName,
                    lastName,
                    metadata: {
                        platform: 'massview',
                        signup: { interested_in: accountInfo.get('interestedIn') },
                    },
                    password,
                    passwordRepeat: password,
                }));

                setJwt(signUpRes.payload.data.token);
                const meRes = await fetchMe();

                if (accountInfo.get('companyName')) {
                    await patchCompany(
                        meRes.getIn(['company', 'id']),
                        { name: accountInfo.get('companyName') }
                    );
                }

                const subscriptionRes = await postSubscription({ stripePlan: freePlan.get('planId') });
                const { data } = subscriptionRes.payload;

                onSignUp(data.company.users[0], data.plan);
                fetchMe();
                setShowLoading(false);
                setCurrentStep('product');
            },
            title: 'Create an Account',
            validateInputs: () => {
                setValidation(accountInfoValidation);

                return accountInfoValidation;
            },
        },
        product: {
            buttonText: 'Finish',
            component: r(ProductSetup, {
                form: productSetupForm,
                selectedPlan: freePlan,
                updateForm: setProductSetupForm,
                validation,
            }),
            disabled: !asin || !asin.length === 10,
            isLoading: showLoading,
            onNext: () => {
                const keyword = productSetupForm.get('keyword');

                setShowLoading(true);
                createListing(
                    ListingRecord({
                        isMonitored: true,
                        url: `https://www.amazon.com/gp/product/${asin}`,
                    })
                ).then(
                    (res) => {
                        if (keyword) {
                            postKeyword(
                                {
                                    asin,
                                    keyword,
                                    productId: res.payload.data.product.id,
                                },
                                { embeds: 'product.keywords,product.listings,product.media' }
                            );
                        }

                        return Promise.resolve();
                    }
                ).finally(() => {
                    if (keyword) {
                        trackEvent('Keyword Tracked', { asin, keyword });
                    }

                    trackEvent('Product Tracked', { asin });
                    navigate('/dashboard');
                    setShowLoading(false);
                });
            },
            title: 'Product Setup',
            validateInputs: () => {
                setValidation(productSetupValidation);

                return productSetupValidation;
            },
        },
    });
    const stepKeys = steps.keySeq();
    const stepKeyIndex = stepKeys.findIndex((key) => key === currentStep);

    useEffect(() => {
        if (show && isLoggedIn) {
            if (!hasProducts) {
                setCurrentStep('product');
            } else {
                navigate('/dashboard');
            }
        }
    }, [isLoggedIn, show]);

    useEffect(() => {
        if (!steps.get(currentStep)) {
            setCurrentStep(stepKeys.get(0));
        }

        setValidation(Map());
    }, [currentStep]);

    const {
        buttonText,
        component,
        disabled = false,
        isLoading = false,
        onNext,
        title,
        validateInputs = () => Map(),
    } = steps.get(currentStep);

    return (
        r(OverlayCard, { onClose, show, showSkeleton, title }, [
            r(CloseButton, { isRendered: onClose, onClick: () => onClose() }),
            r(ComponentContent, [component]),
            r(Footer, [
                r(Flex, { flex: 1 }),
                r(Flex, { flex: 2 }, [
                    r(Indicator, { currentStep: stepKeyIndex, steps: stepKeys }),
                ]),
                r(Flex, { alignItems: 'center', flex: 1, justifyContent: 'flex-end' }, [
                    r(Skip, { isRendered: title === 'Product Setup' }, [
                        'Skip',
                    ]),
                    r(Button, {
                        disabled,
                        isLoading,
                        label: buttonText,
                        onClick: () => {
                            const currentValidation = validateInputs();

                            if (!currentValidation.size && !disabled && onNext) {
                                onNext();
                            }
                        },
                        primary: true,
                        style: (() => {
                            if (title === 'Payment') {
                                return { background: '#03DE74', color: '#000' };
                            }

                            return {};
                        })(),
                        type: 'button'
                    }),
                ]),
            ]),
            r(Flex, [
                r(Agreement, [
                    'By continuing you agree to the ',
                    r(Link, { to: '/terms' }, ['terms of service']),
                    ' and ',
                    r(Link, { to: '/privacy' }, ['privacy policy']),
                ]),
            ]),
        ])
    );
}

AccountSetup.propTypes = propTypes;
